import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RenderEtaClock, renderEtaLine } from '../render-eta';
import { RenderJob, renderPreviewStage } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { RenderNotifyService } from '../render-notify.service';
import { fileExtension, formatBytes } from '../render-upload.inspector';
import { RENDER_OUTPUT_SPEC, RENDER_PREVIEW_ETA, RENDER_PREVIEW_STAGES, RENDER_UPLOAD_BAR, renderSampleById } from '../render.data';
import { RenderStepBase } from './render-step-base';

// A line of the processing card as it is drawn: 'completing' is the spinning arc closing into a
// ring, just before the check lands.
export type RenderCardRowState = 'pending' | 'current' | 'completing' | 'done';

interface RenderCardRow {
  label: string;
  doneLabel: string;
}

// The card's bar follows RenderJob.progress like a critically damped spring, never faster than
// MAX_SPEED (% a second) and never back: a jump to the next stage glides in about a second and a
// half, the time creep's one-percent steps blur into one steady movement. The finale (the preview
// is in, RENDER_PREVIEW_FINALE_MS) runs it tighter, as fast as the rest of the bar needs to be
// done in FINALE_GLIDE_S, and no slower than FINALE_MAX_SPEED.
const BAR_STIFFNESS = 3;
const BAR_MAX_SPEED = 15;
const FINALE_STIFFNESS = 4.5;
const FINALE_MAX_SPEED = 30;
const FINALE_GLIDE_S = 0.9;
// A line closes as the bar comes to its end (ROW_LEAD short of it, so the check lands with the
// bar, not after its slow last stretch): its arc closes into a ring (ROW_CLOSE_MS), the check
// lands, and the next line may not close before ROW_SETTLE_MS more — so a report that skips
// stages ticks them one after the other, never two at once. The last line closes on the bar's
// last stretch, in the finale.
const ROW_LEAD = 2.5;
const ROW_CLOSE_MS = 420;
const ROW_SETTLE_MS = 380;
const LAST_ROW_BAR = 97;
// The time left is re-read this often between the renderer's frames: its line moves in minutes.
const ETA_TICK_MS = 15000;

// Two waits: the preview render and the full 360° render after payment (the step the prototype
// did not have — there the preview already was the video). `mode` comes from the route data.
//
// The preview's wait is the preview step's layout with nothing on the screens yet: no frame
// exists before the render ends, so none is shown (owner, 2026-09-23). The card lists the stages;
// the preview step then develops the frames on these same screens. The full render keeps both
// frames the user has already seen, on the same devices, and "when your videos are ready" beside
// them (Figma 28574:58489): an email and a notification from this browser.
@Component({
  selector: 'app-render-progress-step',
  templateUrl: './render-progress-step.component.html',
})
export class RenderProgressStepComponent extends RenderStepBase implements OnDestroy {
  @HostBinding('class') public readonly hostClass = 'render-step step-progress';
  // The render's page straight after the payment comes in, as the preview's does after its wait.
  @HostBinding('class.is-arriving') public arriving = false;

  @Input() mode: 'processing' | 'rendering' = 'processing';

  public readonly seconds = RENDER_OUTPUT_SPEC.seconds;
  public readonly stages = RENDER_PREVIEW_STAGES;
  public readonly eta = RENDER_PREVIEW_ETA;

  // The videos' time left (render-eta.ts).
  public etaLine = renderEtaLine({ kind: 'usual' });
  private etaClock = new RenderEtaClock();
  private etaModelId = '';
  private etaTicker = 0;
  private etaWarned = false;

  // The upload's line, then the stages.
  public readonly rows: ReadonlyArray<RenderCardRow> = [{ label: 'Uploading', doneLabel: 'Uploaded' }, ...RENDER_PREVIEW_STAGES];

  // The browser's answer is awaited once per click.
  public askingBrowser = false;

  // What the card shows, a step behind the job at most (see BAR_STIFFNESS and ROW_CLOSE_MS): the
  // bar's position and speed, the lines drawn ticked (-1 until the first job), the line closing
  // and when it is done closing, and when the next one may start to.
  @ViewChild('barFill') private barFill?: ElementRef<HTMLElement>;
  private shownBar = 0;
  private barSpeed = 0;
  private drawnBar = -1;
  private shownDone = -1;
  private closing = -1;
  private closedAt = 0;
  private settledAt = 0;
  private frame = 0;
  private lastFrameAt = 0;
  // The finale's top speed, set when it starts: what is left of the bar over FINALE_GLIDE_S.
  private finaleSpeed = 0;
  private arrivalChecked = false;

  constructor(
    route: ActivatedRoute,
    router: Router,
    flow: RenderFlowService,
    private readonly notify: RenderNotifyService,
    private readonly zone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
  ) {
    super(route, router, flow);
    this.mode = (route.snapshot.data['mode'] as 'processing' | 'rendering') || 'processing';
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    cancelAnimationFrame(this.frame);
    clearInterval(this.etaTicker);
  }

  // The first job is drawn where it stands — a page opened mid-way does not replay what is behind
  // it; from then on the card follows it frame by frame.
  protected override onJob(job: RenderJob): void {
    // Only the render's page takes the reveal: the preview's wait still on screen as the preview
    // lands must leave it to the preview step.
    if (!this.arrivalChecked) {
      this.arrivalChecked = true;
      this.arriving = this.isRendering && this.flow.takeReveal(job.modelId);
    }
    if (this.isRendering) {
      this.followEta(job);
      return;
    }
    if (this.shownDone >= 0) {
      return;
    }
    this.shownBar = job.progress;
    this.shownDone = this.targetDone();
    this.zone.runOutsideAngular(() => (this.frame = requestAnimationFrame(this.animate)));
  }

  private followEta(job: RenderJob): void {
    if (job.modelId !== this.etaModelId) {
      this.etaModelId = job.modelId;
      this.etaClock = new RenderEtaClock();
    }
    const now = Date.now();
    this.etaClock.update(job.eta, now);
    this.etaLine = renderEtaLine(this.etaClock.view(now));
    // A value that makes no sense never reaches the page, but somebody should hear of it, once.
    const wrong = job.eta?.sides.find((side) => side.state === 'unknown' && side.rejected);
    if (wrong && !this.etaWarned) {
      this.etaWarned = true;
      console.warn(`Render flow: time left falls back to the usual, ${job.modelId} has`, wrong);
    }
    if (!this.etaTicker) {
      this.etaTicker = this.zone.runOutsideAngular(() => window.setInterval(() => {
        const line = renderEtaLine(this.etaClock.view(Date.now()));
        if (line !== this.etaLine) {
          this.etaLine = line;
          this.changeDetector.detectChanges();
        }
      }, ETA_TICK_MS));
    }
  }

  private readonly animate = (now: number): void => {
    // A hidden tab gets no frames: back in it, the card catches up at its own pace.
    const seconds = this.lastFrameAt ? Math.min(0.05, (now - this.lastFrameAt) / 1000) : 0;
    this.lastFrameAt = now;
    this.moveBar(seconds);
    if (this.moveRows(now)) {
      this.changeDetector.detectChanges();
    }
    this.frame = requestAnimationFrame(this.animate);
  };

  // The preview is in: the card is playing its finale before the preview step opens.
  private get finale(): boolean {
    return this.job?.step === 'preview';
  }

  private get barTarget(): number {
    return this.finale ? 100 : this.barProgress;
  }

  private moveBar(seconds: number): void {
    const target = this.barTarget;
    if (target <= this.shownBar) {
      this.barSpeed = 0;
    } else {
      if (this.finale && !this.finaleSpeed) {
        this.finaleSpeed = Math.max(FINALE_MAX_SPEED, (100 - this.shownBar) / FINALE_GLIDE_S);
      }
      const stiffness = this.finale ? FINALE_STIFFNESS : BAR_STIFFNESS;
      const pull = stiffness * stiffness * (target - this.shownBar) - 2 * stiffness * this.barSpeed;
      this.barSpeed = Math.min(this.finale ? this.finaleSpeed : BAR_MAX_SPEED, Math.max(0, this.barSpeed + pull * seconds));
      this.shownBar = Math.min(target, this.shownBar + this.barSpeed * seconds);
    }
    const fill = this.barFill?.nativeElement;
    if (fill && Math.abs(this.shownBar - this.drawnBar) > 0.01) {
      fill.style.transform = `translateX(${(this.shownBar - 100).toFixed(2)}%)`;
      this.drawnBar = this.shownBar;
    }
  }

  // Whether a line changed: ticked, closing, or taken back.
  private moveRows(now: number): boolean {
    const target = this.targetDone();
    // The renderer went back: drawn as it is, at once (owner, 2026-10-20: statuses are never
    // smoothed — a step back is a bug to see). The bar stays where it was.
    if (target < this.shownDone || (this.closing >= 0 && target <= this.closing)) {
      const shownDone = Math.min(this.shownDone, target);
      const changed = shownDone !== this.shownDone || this.closing >= 0;
      this.shownDone = shownDone;
      this.closing = -1;
      return changed;
    }
    if (this.closing >= 0) {
      if (now < this.closedAt) {
        return false;
      }
      this.shownDone = this.closing + 1;
      this.closing = -1;
      this.settledAt = now + ROW_SETTLE_MS;
      return true;
    }
    if (target <= this.shownDone || now < this.settledAt) {
      return false;
    }
    // The bar first reaches the line's end — or as far as it goes, should the job say less.
    const end = this.shownDone === this.rows.length - 1 ? LAST_ROW_BAR : this.rowEnd(this.shownDone);
    if (this.shownBar < Math.min(end, this.barTarget) - ROW_LEAD) {
      return false;
    }
    this.closing = this.shownDone;
    this.closedAt = now + ROW_CLOSE_MS;
    return true;
  }

  // Where the bar stands when a line is done: the upload's tenth, then the next stage's start.
  private rowEnd(row: number): number {
    return row === 0 ? RENDER_UPLOAD_BAR : RENDER_PREVIEW_STAGES[row]?.from ?? 100;
  }

  // How many lines the job has done, from the top: the upload, then the stages (stageState).
  private targetDone(): number {
    if (this.isUploading) {
      return 0;
    }
    if (this.finale) {
      return this.rows.length;
    }
    let done = 1;
    while (done < this.rows.length && this.stageState(done - 1) === 'done') {
      done++;
    }
    return done;
  }

  // A line as the card draws it: ticked up to what has closed, the one after it current once the
  // job has started it.
  public rowState(row: number): RenderCardRowState {
    const shownDone = this.shownDone < 0 ? this.targetDone() : this.shownDone;
    if (row < shownDone) {
      return 'done';
    }
    if (row === this.closing) {
      return 'completing';
    }
    if (row > shownDone) {
      return 'pending';
    }
    return row === 0 || this.stageState(row - 1) !== 'pending' ? 'current' : 'pending';
  }

  public rowLabel(row: number): string {
    return this.rowState(row) === 'done' ? this.rows[row].doneLabel : this.rows[row].label;
  }

  // What a line says on its right: the upload's bytes, what the model turned out to be, one frame
  // of two.
  public rowDetail(row: number): string {
    const state = this.rowState(row);
    if (row === 0) {
      return this.uploadDetail;
    }
    if (row === 2 && state === 'done') {
      return this.checkedDetail;
    }
    if (row === this.rows.length - 1 && state !== 'done' && state !== 'pending' && this.framesDone === 1) {
      return '1 of 2';
    }
    return '';
  }

  // Under an upload's empty screens: what the upload page found about the files ("From big.zip: …"),
  // and that the mock renders nothing for it.
  public get uploadNote(): string | null {
    const mock = this.isUpload && this.flow.isMock ? 'Uploads are switched off in this build — no render is being generated for your file.' : '';
    return [this.job?.uploadNote, mock].filter(Boolean).join(' ') || null;
  }

  // The upload is still on its way: it is the card's current line, and this tab has to stay open.
  public get isUploading(): boolean {
    return !!this.job?.uploading;
  }

  // One bar from the first byte to the frames (RenderJob.progress): the header's ring says the same.
  public get barProgress(): number {
    return this.job?.progress ?? 0;
  }

  // The renderer goes on with the tab closed; only the mock's run lives in the page.
  public get runsWithoutPage(): boolean {
    return !this.flow.isMock;
  }

  // The mock sends no email: its "Stand-in" line. The render backend (kind 'simulator', on dev and
  // prod alike) sends them itself, so the line said so wrongly there until 2026-11-02.
  public get isMock(): boolean {
    return this.flow.isMock;
  }

  public get email(): string | null {
    return this.notify.email;
  }

  public get emailOn(): boolean {
    return !!this.email && this.notify.settings.email;
  }

  public get browserOn(): boolean {
    return this.notify.browserOn;
  }

  // Off and out of reach when the browser cannot or will not show notifications.
  public get browserBlocked(): boolean {
    const permission = this.notify.permission;
    return permission === 'denied' || permission === 'unsupported';
  }

  public get browserNote(): string {
    switch (this.notify.permission) {
      case 'denied':
        return 'Blocked in your browser settings';
      case 'unsupported':
        return 'Not available in this browser';
      default:
        return this.browserOn ? 'While a 3DWay tab is open' : '';
    }
  }

  public onEmailChange(event: Event): void {
    this.notify.setEmail((event.target as HTMLInputElement).checked);
  }

  public async onBrowserChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    this.askingBrowser = true;
    try {
      // What the browser allowed wins over the tick the click left.
      input.checked = await this.notify.setBrowser(input.checked);
    } finally {
      this.askingBrowser = false;
    }
  }

  public get isRendering(): boolean {
    return this.mode === 'rendering';
  }

  // Frames already done, of the two (the dev render backend only).
  public get framesDone(): number {
    const sides = this.job?.sides;
    return sides ? [sides.landscape, sides.portrait].filter((phase) => phase === 'done').length : 0;
  }

  // The screens say what the card's current line says, as the card draws it: uploading, setting up
  // the studio, checking the model, rendering. A screen whose own job is further along waits for
  // the card (still-devices caps it); one that is behind says so.
  public get screenState(): 'uploading' | 'setup' | 'checking' | 'rendering' {
    const done = this.shownDone < 0 ? this.targetDone() : this.shownDone;
    if (done === 0) {
      return 'uploading';
    }
    return done >= 3 ? 'rendering' : done === 2 ? 'checking' : 'setup';
  }

  // The stages after the upload, each ticked by what the renderer really reports (decision 40):
  // a GPU job started, the model opened, both frames saved. Waiting for a GPU is setting up the
  // studio, never a line of its own (owner, 2026-10-20: the user is not told about free renderers).
  public stageState(index: number): 'done' | 'current' | 'pending' {
    if (this.isUploading) {
      return 'pending';
    }
    // The dev render backend says where each orientation stands (renderPreviewStage).
    const sides = this.job?.sides;
    if (sides) {
      if (sides.landscape === 'done' && sides.portrait === 'done') {
        return 'done';
      }
      const current = renderPreviewStage(sides);
      return index < current ? 'done' : index === current ? 'current' : 'pending';
    }
    const progress = this.job?.progress ?? 0;
    const next = this.stages[index + 1];
    if (next && progress >= next.from) {
      return 'done';
    }
    return progress >= this.stages[index].from ? 'current' : 'pending';
  }

  // The upload's line: the bytes on their way, then what was sent — facts about the model.
  public get uploadDetail(): string {
    const uploading = this.job?.uploading;
    if (uploading) {
      return `${uploading.sentBytes > 0 ? formatBytes(uploading.sentBytes) : '0 MB'} of ${formatBytes(uploading.totalBytes)}`;
    }
    const source = this.job?.source;
    if (!source) {
      return '';
    }
    const bytes = source.kind === 'upload' ? source.totalBytes : renderSampleById(source.sampleId)?.fileBytes;
    return bytes ? formatBytes(bytes) : '';
  }

  public get checkedDetail(): string {
    const source = this.job?.source;
    if (!source) {
      return '';
    }
    if (source.kind === 'sample') {
      return fileExtension(renderSampleById(source.sampleId)?.fileName || '').slice(1).toUpperCase();
    }
    const textures = source.textureCount ? ` · ${source.textureCount} texture${source.textureCount === 1 ? '' : 's'}` : '';
    return `${source.format.toUpperCase()}${textures}`;
  }

  public onCancel(): void {
    if (!this.job) {
      return;
    }
    this.flow.cancelPreview(this.job.modelId);
  }
}
